import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Circle,
  Eraser,
  Highlighter,
  MessageSquarePlus,
  MousePointer2,
  Pencil,
  Square,
  Trash2,
  Type,
} from 'lucide-react';
import {
  SOP_IMAGE_MARKUP_COLORS,
  isImageMarkupType,
  type SopMarkupAnnotation,
  type SopImageMarkupType,
} from './types.ts';

type Tool =
  | 'select'
  | 'rect'
  | 'ellipse'
  | 'arrow'
  | 'pen'
  | 'text'
  | 'callout'
  | 'pin'
  | 'erase';

type Props = {
  frameId: string;
  sectionId: string;
  sectionHeading: string;
  src: string;
  alt?: string;
  annotations: SopMarkupAnnotation[];
  markupMode: boolean;
  canEdit: boolean;
  onAdd: (item: SopMarkupAnnotation) => void;
  onRemove: (id: string) => void;
};

type DraftShape =
  | { tool: 'rect' | 'ellipse'; x0: number; y0: number; x1: number; y1: number }
  | { tool: 'arrow'; x0: number; y0: number; x1: number; y1: number }
  | { tool: 'pen'; points: number[][] }
  | null;

function newId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0')}`.slice(0, 36);
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function normBox(x0: number, y0: number, x1: number, y1: number) {
  const x = clamp01(Math.min(x0, x1));
  const y = clamp01(Math.min(y0, y1));
  const w = clamp01(Math.max(x0, x1)) - x;
  const h = clamp01(Math.max(y0, y1)) - y;
  return { x, y, w, h };
}

function strokeColor(c?: string): string {
  if (c && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c)) return c;
  return SOP_IMAGE_MARKUP_COLORS[0];
}

/** Anchor point (0–1) for placing an on-image note bubble near a mark. */
function markNoteAnchor(a: SopMarkupAnnotation): { x: number; y: number } {
  if ((a.type === 'image_rect' || a.type === 'image_ellipse') && a.x != null && a.y != null) {
    return { x: clamp01(a.x + (a.w ?? 0) * 0.5), y: clamp01(a.y) };
  }
  if (a.type === 'image_arrow' && a.x2 != null && a.y2 != null) {
    return { x: clamp01(a.x2), y: clamp01(a.y2) };
  }
  if (a.type === 'image_pen' && Array.isArray(a.points) && a.points.length > 0) {
    const last = a.points[a.points.length - 1];
    return { x: clamp01(last?.[0] ?? 0.5), y: clamp01(last?.[1] ?? 0.5) };
  }
  return { x: clamp01(a.x ?? 0.5), y: clamp01(a.y ?? 0.5) };
}

const TOOLS: { id: Tool; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'select', label: 'Select', Icon: MousePointer2 },
  { id: 'rect', label: 'Box', Icon: Square },
  { id: 'ellipse', label: 'Ellipse', Icon: Circle },
  { id: 'arrow', label: 'Arrow', Icon: ArrowRight },
  { id: 'pen', label: 'Pen', Icon: Pencil },
  { id: 'text', label: 'Text', Icon: Type },
  { id: 'callout', label: 'Callout', Icon: Highlighter },
  { id: 'pin', label: 'Comment', Icon: MessageSquarePlus },
  { id: 'erase', label: 'Erase', Icon: Eraser },
];

/** Keep last tool/color across remounts (markup save re-renders markdown → overlay remount). */
let stickyTool: Tool = 'rect';
let stickyColor: string = SOP_IMAGE_MARKUP_COLORS[0];

const SopImageMarkupOverlay: React.FC<Props> = ({
  frameId,
  sectionId,
  sectionHeading,
  src,
  alt,
  annotations,
  markupMode,
  canEdit,
  onAdd,
  onRemove,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tool, setToolState] = useState<Tool>(stickyTool);
  const setTool = useCallback((next: Tool) => {
    stickyTool = next;
    setToolState(next);
  }, []);
  const toolRef = useRef<Tool>(tool);
  toolRef.current = tool;
  const [color, setColorState] = useState<string>(stickyColor);
  const setColor = useCallback((next: string) => {
    stickyColor = next;
    setColorState(next);
  }, []);
  const [draft, setDraft] = useState<DraftShape>(null);
  const [drawing, setDrawing] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [textPrompt, setTextPrompt] = useState<{ x: number; y: number; kind: 'text' | 'pin' | 'edit'; editId?: string } | null>(
    null,
  );
  const [textDraft, setTextDraft] = useState('');

  const frameMarks = useMemo(
    () =>
      annotations.filter(
        (a) => a.section_id === sectionId && a.frame_id === frameId && isImageMarkupType(a.type),
      ),
    [annotations, sectionId, frameId],
  );

  const editable = markupMode && canEdit;

  const toNorm = useCallback((clientX: number, clientY: number) => {
    const el = wrapRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return { x: 0, y: 0 };
    return {
      x: clamp01((clientX - r.left) / r.width),
      y: clamp01((clientY - r.top) / r.height),
    };
  }, []);

  const nextCalloutNumber = useCallback(() => {
    const nums = frameMarks
      .filter((a) => a.type === 'image_callout' && typeof a.callout_number === 'number')
      .map((a) => a.callout_number as number);
    return (nums.length ? Math.max(...nums) : 0) + 1;
  }, [frameMarks]);

  const baseAnn = useCallback(
    (type: SopImageMarkupType, extra: Partial<SopMarkupAnnotation> = {}): SopMarkupAnnotation => ({
      id: newId(),
      section_id: sectionId,
      section_heading: sectionHeading,
      type,
      frame_id: frameId,
      color,
      created_at: new Date().toISOString(),
      ...extra,
    }),
    [sectionId, sectionHeading, frameId, color],
  );

  const finishDraft = useCallback(
    (d: DraftShape) => {
      if (!d) return;
      if (d.tool === 'rect' || d.tool === 'ellipse') {
        const box = normBox(d.x0, d.y0, d.x1, d.y1);
        if (box.w < 0.01 || box.h < 0.01) return;
        onAdd(
          baseAnn(d.tool === 'rect' ? 'image_rect' : 'image_ellipse', {
            x: box.x,
            y: box.y,
            w: box.w,
            h: box.h,
          }),
        );
        return;
      }
      if (d.tool === 'arrow') {
        const dx = Math.abs(d.x1 - d.x0);
        const dy = Math.abs(d.y1 - d.y0);
        if (dx < 0.01 && dy < 0.01) return;
        onAdd(
          baseAnn('image_arrow', {
            x1: clamp01(d.x0),
            y1: clamp01(d.y0),
            x2: clamp01(d.x1),
            y2: clamp01(d.y1),
          }),
        );
        return;
      }
      if (d.tool === 'pen') {
        if (d.points.length < 2) return;
        // Downsample long strokes to keep payload small.
        const pts =
          d.points.length > 80
            ? d.points.filter((_, i) => i % Math.ceil(d.points.length / 80) === 0 || i === d.points.length - 1)
            : d.points;
        onAdd(baseAnn('image_pen', { points: pts.map(([x, y]) => [clamp01(x), clamp01(y)]) }));
      }
    },
    [baseAnn, onAdd],
  );

  const markIdFromEventTarget = (target: EventTarget | null): string | null => {
    const el = target as Element | null;
    if (!el || typeof el.closest !== 'function') return null;
    return el.closest('[data-mark-id]')?.getAttribute('data-mark-id') ?? null;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!editable) return;
    if (textPrompt) return;
    e.stopPropagation();
    const currentTool = toolRef.current;
    const hitId = markIdFromEventTarget(e.target);

    // Select / Erase: handle hit on pointerdown (do NOT preventDefault — that blocks clicks).
    if (currentTool === 'erase' || currentTool === 'select') {
      if (hitId) {
        if (currentTool === 'erase') {
          onRemove(hitId);
          setSelectedId(null);
        } else {
          setSelectedId(hitId);
        }
      } else {
        setSelectedId(null);
      }
      return;
    }

    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    const p = toNorm(e.clientX, e.clientY);

    if (currentTool === 'text' || currentTool === 'pin') {
      setTextPrompt({ x: p.x, y: p.y, kind: currentTool === 'text' ? 'text' : 'pin' });
      setTextDraft('');
      return;
    }
    if (currentTool === 'callout') {
      const n = nextCalloutNumber();
      onAdd(
        baseAnn('image_callout', {
          x: p.x,
          y: p.y,
          w: 0.04,
          h: 0.04,
          callout_number: n,
          text: String(n),
        }),
      );
      return;
    }
    if (currentTool === 'pen') {
      setDrawing(true);
      setDraft({ tool: 'pen', points: [[p.x, p.y]] });
      return;
    }
    if (currentTool === 'rect' || currentTool === 'ellipse' || currentTool === 'arrow') {
      setDrawing(true);
      setDraft({ tool: currentTool, x0: p.x, y0: p.y, x1: p.x, y1: p.y });
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!editable || !drawing || !draft) return;
    e.preventDefault();
    e.stopPropagation();
    const p = toNorm(e.clientX, e.clientY);
    if (draft.tool === 'pen') {
      const last = draft.points[draft.points.length - 1];
      if (last && Math.hypot(p.x - last[0], p.y - last[1]) < 0.004) return;
      setDraft({ tool: 'pen', points: [...draft.points, [p.x, p.y]] });
      return;
    }
    setDraft({ ...draft, x1: p.x, y1: p.y });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!editable || !drawing) return;
    e.preventDefault();
    e.stopPropagation();
    setDrawing(false);
    if (draft) finishDraft(draft);
    setDraft(null);
  };

  const selected = frameMarks.find((a) => a.id === selectedId) ?? null;

  return (
    <div className="my-2 block" data-sop-image-markup="1">
      {editable && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-1">
          {TOOLS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              title={label}
              aria-label={label}
              aria-pressed={tool === id}
              onClick={() => {
                setTool(id);
                if (id !== 'select') setSelectedId(null);
                setTextPrompt(null);
              }}
              className={`inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium ${
                tool === id ? 'bg-white text-blue-800 shadow-sm ring-1 ring-blue-200' : 'text-gray-600 hover:bg-white/80'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
          <span className="mx-1 h-4 w-px bg-gray-300" />
          {SOP_IMAGE_MARKUP_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              title={c}
              aria-label={`Color ${c}`}
              onClick={() => setColor(c)}
              className={`h-4 w-4 rounded-full border ${color === c ? 'ring-2 ring-offset-1 ring-blue-500' : 'border-gray-300'}`}
              style={{ backgroundColor: c }}
            />
          ))}
          {selected ? (
            <>
              <span className="mx-1 h-4 w-px bg-gray-300" />
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded bg-sky-100 px-2 py-1 text-[11px] font-semibold text-sky-900 ring-1 ring-sky-300 hover:bg-sky-200"
                onClick={() => {
                  const anchor = markNoteAnchor(selected);
                  setTextPrompt({ x: anchor.x, y: anchor.y, kind: 'edit', editId: selected.id });
                  setTextDraft(selected.comment || selected.text || '');
                }}
              >
                <MessageSquarePlus className="h-3.5 w-3.5" /> Note
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-1 text-[11px] font-semibold text-red-800 ring-1 ring-red-300 hover:bg-red-200"
                onClick={() => {
                  onRemove(selected.id);
                  setSelectedId(null);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </>
          ) : tool === 'select' ? (
            <span className="ml-1 text-[10px] text-gray-500">Click a mark on the image</span>
          ) : null}
        </div>
      )}

      <div ref={wrapRef} className="relative inline-block max-w-full">
        <img src={src} alt={alt ?? ''} loading="lazy" className="max-h-96 max-w-full rounded border border-gray-200" draggable={false} />
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{
            pointerEvents: editable || frameMarks.length > 0 ? 'auto' : 'none',
            cursor: editable
              ? tool === 'erase'
                ? 'cell'
                : tool === 'select'
                  ? 'default'
                  : 'crosshair'
              : 'default',
            touchAction: editable ? 'none' : 'auto',
          }}
        >
          <defs>
            {frameMarks
              .filter((a) => a.type === 'image_arrow')
              .map((a) => (
                <marker
                  key={`m-${a.id}`}
                  id={`sop-arrow-${a.id}`}
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M0,0 L6,3 L0,6 Z" fill={strokeColor(a.color)} />
                </marker>
              ))}
            {draft?.tool === 'arrow' && (
              <marker id="sop-arrow-draft" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L6,3 L0,6 Z" fill={color} />
              </marker>
            )}
          </defs>

          {frameMarks.map((a) => {
            const c = strokeColor(a.color);
            const isSel = selectedId === a.id;
            // Selection = thicker dashed stroke only (no CSS outline — that paints the whole image).
            const selectedStroke = isSel ? 0.012 : 0.006;
            const hitStyle = {
              cursor: editable && (tool === 'erase' || tool === 'select') ? 'pointer' : 'inherit',
            } as React.CSSProperties;
            const markProps = { 'data-mark-id': a.id, style: hitStyle };

            if (a.type === 'image_rect' && a.w != null && a.h != null && a.x != null && a.y != null) {
              return (
                <rect
                  key={a.id}
                  {...markProps}
                  x={a.x}
                  y={a.y}
                  width={a.w}
                  height={a.h}
                  fill={`${c}22`}
                  stroke={isSel ? '#2563eb' : c}
                  strokeWidth={selectedStroke}
                  strokeDasharray={isSel ? '0.02 0.012' : undefined}
                  vectorEffect="non-scaling-stroke"
                />
              );
            }
            if (a.type === 'image_ellipse' && a.w != null && a.h != null && a.x != null && a.y != null) {
              return (
                <ellipse
                  key={a.id}
                  {...markProps}
                  cx={a.x + a.w / 2}
                  cy={a.y + a.h / 2}
                  rx={a.w / 2}
                  ry={a.h / 2}
                  fill={`${c}22`}
                  stroke={isSel ? '#2563eb' : c}
                  strokeWidth={selectedStroke}
                  strokeDasharray={isSel ? '0.02 0.012' : undefined}
                  vectorEffect="non-scaling-stroke"
                />
              );
            }
            if (a.type === 'image_arrow' && a.x1 != null && a.y1 != null && a.x2 != null && a.y2 != null) {
              // Stroke widths are in viewBox units (0–1). Do NOT use non-scaling-stroke here —
              // that treats strokeWidth as CSS px, so 0.006 ≈ invisible (boxes still showed via fill).
              return (
                <g key={a.id} data-mark-id={a.id} style={hitStyle}>
                  {/* Wider invisible hit area */}
                  <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="transparent" strokeWidth={0.04} />
                  <line
                    x1={a.x1}
                    y1={a.y1}
                    x2={a.x2}
                    y2={a.y2}
                    stroke={isSel ? '#2563eb' : c}
                    strokeWidth={selectedStroke}
                    markerEnd={`url(#sop-arrow-${a.id})`}
                  />
                </g>
              );
            }
            if (a.type === 'image_pen' && Array.isArray(a.points) && a.points.length > 1) {
              const d = a.points.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt[0]} ${pt[1]}`).join(' ');
              return (
                <g key={a.id} data-mark-id={a.id} style={hitStyle}>
                  <path
                    d={d}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={0.04}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d={d}
                    fill="none"
                    stroke={isSel ? '#2563eb' : c}
                    strokeWidth={selectedStroke}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              );
            }
            if (a.type === 'image_text' && a.x != null && a.y != null) {
              return (
                <g key={a.id} data-mark-id={a.id} style={hitStyle}>
                  <rect
                    x={a.x}
                    y={a.y}
                    width={0.22}
                    height={0.05}
                    fill="#ffffffcc"
                    stroke={isSel ? '#2563eb' : c}
                    strokeWidth={0.004}
                  />
                  <text x={a.x + 0.01} y={a.y + 0.035} fontSize={0.03} fill={c}>
                    {(a.text || '').slice(0, 40)}
                  </text>
                </g>
              );
            }
            if (a.type === 'image_callout' && a.x != null && a.y != null) {
              const n = a.callout_number ?? a.text ?? '?';
              return (
                <g key={a.id} data-mark-id={a.id} style={hitStyle}>
                  <circle cx={a.x} cy={a.y} r={0.025} fill={c} stroke={isSel ? '#2563eb' : '#fff'} strokeWidth={0.004} />
                  <text x={a.x} y={a.y} textAnchor="middle" dominantBaseline="central" fontSize={0.028} fill="#fff" fontWeight="700">
                    {n}
                  </text>
                </g>
              );
            }
            if (a.type === 'image_pin' && a.x != null && a.y != null) {
              return (
                <g key={a.id} data-mark-id={a.id} style={hitStyle}>
                  <circle cx={a.x} cy={a.y} r={0.018} fill={c} stroke={isSel ? '#2563eb' : '#fff'} strokeWidth={0.004} />
                  {a.comment ? <title>{a.comment}</title> : null}
                </g>
              );
            }
            return null;
          })}

          {draft?.tool === 'rect' && (() => {
            const b = normBox(draft.x0, draft.y0, draft.x1, draft.y1);
            return (
              <rect
                x={b.x}
                y={b.y}
                width={b.w}
                height={b.h}
                fill={`${color}22`}
                stroke={color}
                strokeWidth={0.006}
                vectorEffect="non-scaling-stroke"
              />
            );
          })()}
          {draft?.tool === 'ellipse' && (() => {
            const b = normBox(draft.x0, draft.y0, draft.x1, draft.y1);
            return (
              <ellipse
                cx={b.x + b.w / 2}
                cy={b.y + b.h / 2}
                rx={b.w / 2}
                ry={b.h / 2}
                fill={`${color}22`}
                stroke={color}
                strokeWidth={0.006}
                vectorEffect="non-scaling-stroke"
              />
            );
          })()}
          {draft?.tool === 'arrow' && (
            <line
              x1={draft.x0}
              y1={draft.y0}
              x2={draft.x1}
              y2={draft.y1}
              stroke={color}
              strokeWidth={0.008}
              markerEnd="url(#sop-arrow-draft)"
            />
          )}
          {draft?.tool === 'pen' && draft.points.length > 1 && (
            <path
              d={draft.points.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt[0]} ${pt[1]}`).join(' ')}
              fill="none"
              stroke={color}
              strokeWidth={0.008}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>

        {/* Notes sit on the image next to their mark (not in a list below). */}
        {frameMarks
          .filter((a) => (a.comment || '').trim())
          .map((a) => {
            const anchor = markNoteAnchor(a);
            const isSel = selectedId === a.id;
            return (
              <div
                key={`note-${a.id}`}
                data-mark-id={a.id}
                className={`absolute z-10 max-w-[11rem] rounded-md border px-2 py-1 text-[11px] shadow-md ${
                  isSel ? 'border-sky-400 bg-sky-50 text-sky-950' : 'border-amber-200 bg-amber-50 text-amber-950'
                }`}
                style={{
                  left: `${Math.min(72, Math.max(1, anchor.x * 100))}%`,
                  top: `${Math.min(85, Math.max(1, anchor.y * 100))}%`,
                  transform: 'translate(6px, -100%)',
                  pointerEvents: editable ? 'auto' : 'none',
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  if (!editable) return;
                  if (toolRef.current === 'erase') {
                    onRemove(a.id);
                    setSelectedId(null);
                    return;
                  }
                  setSelectedId(a.id);
                  setTool('select');
                }}
              >
                <div className="flex items-start gap-1">
                  <span
                    className="mt-1 h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: strokeColor(a.color) }}
                  />
                  <p className="min-w-0 flex-1 break-words">{a.comment}</p>
                  {editable && (
                    <button
                      type="button"
                      className="shrink-0 text-gray-400 hover:text-red-600"
                      aria-label="Remove note"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Clear note text but keep the shape; if pin-only, delete mark.
                        if (a.type === 'image_pin') {
                          onRemove(a.id);
                        } else {
                          onAdd({ ...a, comment: '' });
                        }
                        if (selectedId === a.id) setSelectedId(null);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

        {textPrompt && editable && (
          <div
            className="absolute z-20 w-56 rounded-md border border-gray-200 bg-white p-2 shadow-lg"
            style={{
              left: `${Math.min(75, Math.max(2, textPrompt.x * 100))}%`,
              top: `${Math.min(70, Math.max(2, textPrompt.y * 100))}%`,
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <textarea
              className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
              rows={3}
              maxLength={500}
              autoFocus
              placeholder={textPrompt.kind === 'text' ? 'Label text…' : 'Comment…'}
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
            />
            <div className="mt-1 flex justify-end gap-1">
              <button
                type="button"
                className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                onClick={() => {
                  setTextPrompt(null);
                  setTextDraft('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!textDraft.trim()}
                className="rounded px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: '#1e40af' }}
                onClick={() => {
                  const note = textDraft.trim();
                  if (!note) return;
                  if (textPrompt.kind === 'edit' && textPrompt.editId) {
                    const existing = frameMarks.find((a) => a.id === textPrompt.editId);
                    if (existing) {
                      onAdd({
                        ...existing,
                        comment: note,
                        text: existing.type === 'image_text' ? note : existing.text,
                      });
                    }
                  } else if (textPrompt.kind === 'text') {
                    onAdd(
                      baseAnn('image_text', {
                        x: textPrompt.x,
                        y: textPrompt.y,
                        w: 0.2,
                        h: 0.05,
                        text: note,
                      }),
                    );
                  } else {
                    onAdd(
                      baseAnn('image_pin', {
                        x: textPrompt.x,
                        y: textPrompt.y,
                        comment: note,
                        quote: note.slice(0, 80),
                      }),
                    );
                  }
                  setTextPrompt(null);
                  setTextDraft('');
                }}
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
      {alt ? <span className="mt-1 block text-xs text-gray-500">{alt}</span> : null}
    </div>
  );
};

export default SopImageMarkupOverlay;

